import { CdkMenuTrigger, Menu } from '@angular/cdk/menu';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { FcpButtonColors, FcpButtonSizes, FcpButtonVariants } from '../fcp-button';

@Component({
    selector: 'fcp-button-split',
    templateUrl: './fcp-button-split.component.html',
    styleUrls: ['./fcp-button-split.component.less'],
    standalone: false
})
export class FcpButtonSplitComponent implements AfterViewInit {
    @Input() public menuPosition: ConnectedPosition[];
    @Input() public variant: FcpButtonVariants;
    @Input() public color: FcpButtonColors;
    @Input() public disabled: boolean | 'disabled';
    @Input() public buttonSize: FcpButtonSizes;
    @Input() public menuColor: MenuColor;
    @Input() public menuStyle: MenuStyle;
    @Input() public list: SplitButtonMenuListItem[];
    @Input() public buttonText: string;
    @Input() public iconOnly = false;

    @ViewChild(CdkMenuTrigger) public cdkMenuTrigger: CdkMenuTrigger;
    @ViewChild('mainButton') mainButton: ElementRef;
    @ViewChild('containerRef') containerRef: ElementRef;

    @Output() private onClick: EventEmitter<void>;
    @Output() private menuClosed: EventEmitter<void>;
    @Output() private menuOpened: EventEmitter<void>;
    @Output() private menuItemTriggered: EventEmitter<SplitButtonMenuListItem>;

    public containerWidth: number;

    public fcpButtonVariants = FcpButtonVariants;
    public fcpColor = FcpButtonColors;
    public fcpButtonSize = FcpButtonSizes;
    public fcpMenuColor = MenuColor;
    public fcpMenuStyle = MenuStyle;

    constructor() {
        this.menuPosition = [
            {
                originX: 'start',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'bottom'
            }
        ];
        this.variant = FcpButtonVariants.Filled;
        this.color = FcpButtonColors.Default;
        this.buttonSize = FcpButtonSizes.Medium;
        this.menuColor = MenuColor.Subtle;
        this.menuStyle = MenuStyle.Fluid;
        this.onClick = new EventEmitter<void>();
        this.menuClosed = new EventEmitter<void>();
        this.menuOpened = new EventEmitter<void>();
        this.menuItemTriggered = new EventEmitter<SplitButtonMenuListItem>();
    }

    ngAfterViewInit() {
        this.containerWidth = this.containerRef.nativeElement.offsetWidth;
    }

    public onMainButtonClick() {
        this.onClick.emit();
    }

    public onCdkMenuClosed() {
        this.menuClosed.emit();
    }

    public onCdkMenuOpened() {
        this.menuOpened.emit();
    }

    public close() {
        this.cdkMenuTrigger.close();
    }

    public open() {
        this.cdkMenuTrigger.open();
    }

    public toggle() {
        this.cdkMenuTrigger.toggle();
    }

    public isOpen(): boolean {
        return this.cdkMenuTrigger.isOpen();
    }

    public getMenu(): Menu | undefined {
        return this.cdkMenuTrigger.getMenu();
    }

    public registerChildMenu(child: Menu) {
        return this.cdkMenuTrigger.registerChildMenu(child);
    }

    public onMenuItemTriggered(item: SplitButtonMenuListItem, index: number) {
        if (typeof item?.triggerHandler === 'function') {
            item?.triggerHandler?.(item, index);
        }

        this.menuItemTriggered.emit(item);
    }

    public get separatorStyles(): { [key: string]: boolean } {
        return {
            'fcp-button-container-vertical-line': this.variant === this.fcpButtonVariants.Filled,
            'fcp-button-container-vertical-line-outlined': this.variant === this.fcpButtonVariants.Outlined,
            'fcp-button-container-vertical-line--prominent':
                this.color === this.fcpColor.Prominent && this.variant === this.fcpButtonVariants.Filled,
            'fcp-button-container-vertical-line--subtle':
                this.color === this.fcpColor.Subtle && this.variant === this.fcpButtonVariants.Filled
        };
    }
}

export interface SplitButtonMenuListItem {
    text: string;
    value?: string;
    disabled?: boolean;
    triggerHandler: (item: SplitButtonMenuListItem, index: number) => void;
}

export enum MenuSize {
    Medium = 'medium',
    Large = 'large'
}

export enum MenuColor {
    Prominent = 'prominent',
    Subtle = 'subtle'
}

export enum MenuStyle {
    Fluid = 'fluid',
    Fixed = 'fixed'
}
